import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';

const CookiePolicy: React.FC = () => {
  const theme = useTheme();
  const lastUpdated = 'March 19, 2026';

  const cookieCategories = [
    {
      category: 'Essential',
      purpose: 'Authentication, security, session management',
      examples: 'Session tokens, login cookies',
      duration: 'Session or up to 1 year',
    },
    {
      category: 'Preferences',
      purpose: 'Remember user settings',
      examples: 'Language, theme, interface settings',
      duration: 'Up to 1 year',
    },
    {
      category: 'Analytics',
      purpose: 'Measure usage and improve performance',
      examples: 'Google Analytics (if enabled)',
      duration: 'Up to 2 years',
    },
    {
      category: 'Marketing',
      purpose: 'Track campaign effectiveness',
      examples: 'Conversion tracking, remarketing pixels',
      duration: 'Up to 90 days',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Cookie Policy
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Last updated: {lastUpdated}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: `1px solid ${theme.palette.divider}` }}>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This Cookie Policy explains how Jakatech LLC, a Texas limited liability company doing business as
            Tier One Content ("Jakatech," "we," "us," or "our"), uses cookies and similar technologies when you
            access the Tier One Content platform (the "Service").
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This Policy should be read together with our{' '}
            <MuiLink component={Link} to="/legal/privacy">
              Privacy Policy
            </MuiLink>
            .
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            1. What Are Cookies?
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Cookies are small text files stored on your device when you visit a website or use an online service.
            They help us:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Enable essential functionality
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Remember your preferences
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Improve performance
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Understand usage patterns
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Support marketing efforts (where enabled)
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We also use similar technologies such as local storage and pixels.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            2. Legal Basis for Cookie Use
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Where required by applicable law (including GDPR and EU ePrivacy rules):
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Essential cookies are used based on legitimate interest because they are necessary for the Service to function.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Analytics, preference, and marketing cookies are used only with your consent.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may withdraw your consent at any time.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Withdrawal does not affect the lawfulness of processing based on consent before its withdrawal.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            3. Cookie Consent
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            When you first access the Service, you will be presented with a cookie consent banner allowing you to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Accept all cookies
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Reject non-essential cookies
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Customize cookie preferences
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Non-essential cookies are not activated until you provide consent.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Your consent choices:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Are stored with a timestamp and policy version
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Are securely recorded
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Can be modified at any time
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If we make material changes to our cookie practices, we will request renewed consent where required.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            4. Categories of Cookies We Use
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Examples</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Typical Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cookieCategories.map((row) => (
                  <TableRow key={row.category}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.category}</TableCell>
                    <TableCell>{row.purpose}</TableCell>
                    <TableCell>{row.examples}</TableCell>
                    <TableCell>{row.duration}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Exact cookies may vary depending on features enabled.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            5. Third-Party Cookies
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may use third-party providers that set cookies on our behalf:
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            Google Analytics (if enabled)
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Used to understand how users interact with the Service.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                IP anonymization and consent controls are applied where required.
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            Stripe
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Used for payment processing. Cookies may be set during checkout or payment interactions.
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            Customer Support Tools (e.g., Intercom, if enabled)
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                May use cookies to enable chat functionality and track support interactions.
              </Typography>
            </li>
          </ul>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These providers may process data outside your country. Where required, we rely on appropriate safeguards
            such as Standard Contractual Clauses or the EU–US Data Privacy Framework.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            6. Managing Cookie Preferences
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may manage cookies through:
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            In-App Controls (Recommended)
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Profile → Cookie Preferences
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Privacy Center at{' '}
                <MuiLink component={Link} to="/privacy-center">
                  /privacy-center
                </MuiLink>
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These controls allow granular selection by category.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            Browser Controls
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Most browsers allow you to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                View stored cookies
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Delete cookies
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Block third-party cookies
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Block all cookies
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>Note:</strong> Blocking essential cookies may prevent certain features from functioning.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            7. Do Not Track Signals
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Some browsers transmit "Do Not Track" signals. Because there is no uniform standard for interpreting
            these signals, our Service does not currently respond to them automatically. You can manage tracking
            preferences through our cookie controls.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            8. Impact of Disabling Cookies
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you disable essential cookies:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You may be unable to log in
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Certain features may not function
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Disabling analytics or marketing cookies will not affect core functionality.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            9. International Transfers
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Some cookie-related data may be processed outside your jurisdiction, including in the United States.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Where required, we rely on:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Standard Contractual Clauses (SCCs)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                The EU–US Data Privacy Framework
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            to safeguard international transfers.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            10. Updates to This Policy
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may update this Cookie Policy from time to time.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If changes are material, we will:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Update the "Last Updated" date
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Provide additional notice where required
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Request renewed consent where legally necessary
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            11. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Jakatech LLC<br />
            d/b/a Tier One Content<br />
            10228 E Northwest Hwy #1001<br />
            Dallas, TX 75238<br />
            Email: privacy@tieronecontent.com
          </Typography>

          <Divider sx={{ my: 5 }} />

          <Typography variant="body2" color="text.secondary">
            By using Tier One Content, you acknowledge that you have read and understand this Cookie Policy.
            Last updated: {lastUpdated}.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default CookiePolicy;
